/**
 * React Query hook for fetching user data for match celebration
 */

import type { PublicUserProfile } from "@fitties/shared";
import logger from "@logger";

import { fetchPublicUserProfiles } from "@shared/services/publicUserProfilesService";
import { useQuery } from "@tanstack/react-query";

/**
 * Query key factory for match users
 */
export const matchUsersQueryKeys = {
  all: ["matchUsers"] as const,
  users: (userIds: string[]) => [...matchUsersQueryKeys.all, ...userIds.sort()] as const,
};

/**
 * Hook to fetch public profile data for both users in a match
 */
export function useMatchUsers(userId1: string, userId2: string) {
  const enabled = !!userId1 && !!userId2;

  logger.debug("🔍 useMatchUsers hook", {
    userId1,
    userId2,
    enabled,
    willFetch: enabled,
  });

  return useQuery({
    queryKey: matchUsersQueryKeys.users([userId1, userId2]),
    queryFn: async () => {
      logger.debug("📥 Fetching public profiles for match celebration", { userId1, userId2 });
      const users = await fetchPublicUserProfiles([userId1, userId2]);
      const byUid = new Map(users.map((u) => [u.uid, u]));
      const user1 = byUid.get(userId1);
      const user2 = byUid.get(userId2);

      if (!user1 || !user2) {
        throw new Error("One or both users not found");
      }

      logger.debug("✅ User data fetched for match celebration", {
        user1Name: user1.display_name,
        user2Name: user2.display_name,
      });

      return { user1, user2 } as { user1: PublicUserProfile; user2: PublicUserProfile };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
